import ExcelJS from 'exceljs'
import { readCell, rowsToImportFile, type ImportCell, type ParsedImportFile } from './materialImport'
import type { QualificationTemplate } from '@/api/resourceCapabilitiesApi'

/**
 * Reading and writing the qualification workbook.
 *
 * Parsing only — what a row MEANS is the backend's job, exactly as for the
 * material import. Duplicating the resolution of a person or a method here
 * would give the wizard a second opinion that could disagree with the one that
 * actually writes the rows.
 *
 * Two sheets, because there are two matrices: `Procedures` (who can perform
 * which procedure) and `Methods` (who is qualified in which method, with a
 * preference and a proficiency). Both long-format, one row per assignment —
 * `ResourceMethod` carries two values per pair and one cell cannot hold both.
 */

export const SHEET_NAMES = { procedures: 'Procedures', methods: 'Methods' } as const

export interface ParsedQualificationFile {
  procedures?: ParsedImportFile
  methods?: ParsedImportFile
  /** Problems with the file as a whole, not with a sheet. */
  errors: string[]
}

/** Every row of one worksheet, as a rectangle of cells. */
function readSheet(ws: ExcelJS.Worksheet): ImportCell[][] {
  const rows: ImportCell[][] = []
  for (let r = 1; r <= ws.rowCount; r += 1) {
    const row = ws.getRow(r)
    const cells: ImportCell[] = []
    for (let c = 1; c <= Math.max(row.cellCount, ws.columnCount); c += 1) {
      cells.push(readCell(row.getCell(c).value))
    }
    rows.push(cells)
  }
  return rows
}

/**
 * Find a sheet by name, case-insensitively, falling back to the only sheet.
 *
 * The fallback matters: somebody exports one matrix from their own system and
 * uploads a single unnamed sheet. Refusing that over a tab name would be
 * pedantry, so a lone sheet is taken as whichever matrix the file is for —
 * decided by its HEADERS, not its name, one level up.
 */
function findSheet(wb: ExcelJS.Workbook, name: string): ExcelJS.Worksheet | undefined {
  return wb.worksheets.find((ws) => ws.name.trim().toLowerCase() === name.toLowerCase())
}

/** Does this header row look like the Methods sheet rather than Procedures? */
function looksLikeMethods(headers: ImportCell[]): boolean {
  const keys = headers.map((h) => String(h ?? '').trim().toLowerCase())
  return keys.includes('method')
}

export async function parseQualificationWorkbook(
  buffer: ArrayBuffer,
): Promise<ParsedQualificationFile> {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(buffer)
  if (wb.worksheets.length === 0) {
    return { errors: ['No worksheet found in the file.'] }
  }

  const procedures = findSheet(wb, SHEET_NAMES.procedures)
  const methods = findSheet(wb, SHEET_NAMES.methods)

  if (!procedures && !methods) {
    // One unnamed sheet: decide which matrix it is from its headings.
    const only = wb.worksheets[0]
    const rows = readSheet(only)
    const parsed = rowsToImportFile(rows)
    if (parsed.headers.length === 0) return { errors: parsed.errors }
    return looksLikeMethods(rows[0] ?? [])
      ? { methods: parsed, errors: [] }
      : { procedures: parsed, errors: [] }
  }

  return {
    ...(procedures ? { procedures: rowsToImportFile(readSheet(procedures)) } : {}),
    ...(methods ? { methods: rowsToImportFile(readSheet(methods)) } : {}),
    errors: [],
  }
}

/** The endpoint's wire shape: rows wrapped so each can be validated. */
export function toImportPayload(parsed: ParsedQualificationFile): {
  procedures?: { headers: string[]; rows: { cells: ImportCell[] }[] }
  methods?: { headers: string[]; rows: { cells: ImportCell[] }[] }
} {
  const sheet = (s?: ParsedImportFile) =>
    s && s.headers.length > 0
      ? { headers: s.headers, rows: s.rows.map((cells) => ({ cells })) }
      : undefined
  return { procedures: sheet(parsed.procedures), methods: sheet(parsed.methods) }
}

/**
 * The template workbook: valid choices, pre-filled with the current matrix.
 *
 * Both at once on purpose. A blank template means retyping a hundred rows to
 * change three, which is how an import tool goes unused; pre-filling makes
 * download -> edit -> upload the normal loop, and the upload is merge-only so
 * re-uploading unchanged rows is a no-op rather than a risk.
 *
 * A hidden `Reference` sheet carries the valid people, methods and procedures,
 * and the data-validation dropdowns point at it — so an author picks a real
 * email instead of typing one, which removes most row errors before they exist.
 */
export async function buildQualificationTemplate(
  template: QualificationTemplate,
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook()
  wb.created = new Date()

  const reference = wb.addWorksheet('Reference')
  reference.state = 'veryHidden'
  reference.columns = [
    { header: 'People', key: 'people', width: 34 },
    { header: 'Methods', key: 'methods', width: 30 },
    { header: 'Procedures', key: 'procedures', width: 30 },
  ]
  const depth = Math.max(
    template.people.length,
    template.methods.length,
    template.procedures.length,
  )
  for (let i = 0; i < depth; i += 1) {
    reference.addRow({
      people: template.people[i]?.email ?? null,
      methods: template.methods[i]?.name ?? null,
      procedures: template.procedures[i]?.name ?? null,
    })
  }

  // Excel needs a 1-based, header-offset range, and an empty list has no range
  // at all — a formula over zero rows makes the file unopenable.
  const range = (column: string, count: number) =>
    count > 0 ? [`Reference!$${column}$2:$${column}$${count + 1}`] : undefined

  const procedures = wb.addWorksheet(SHEET_NAMES.procedures)
  procedures.columns = [
    { header: 'Person', key: 'person', width: 34 },
    { header: 'Procedure', key: 'procedure', width: 30 },
    // Last, and written by the export only. Two procedures can share a name, so
    // the id is what lets this file be re-imported; a row typed by hand leaves
    // it blank and is matched by name.
    { header: 'Procedure ID', key: 'procedureId', width: 38 },
  ]
  for (const row of template.assignments.procedures) {
    procedures.addRow(row)
  }

  const methods = wb.addWorksheet(SHEET_NAMES.methods)
  methods.columns = [
    { header: 'Person', key: 'person', width: 34 },
    { header: 'Method', key: 'method', width: 30 },
    { header: 'Preference', key: 'preference', width: 12 },
    { header: 'Proficiency', key: 'proficiency', width: 12 },
    { header: 'Method ID', key: 'methodId', width: 38 },
  ]
  for (const row of template.assignments.methods) {
    methods.addRow(row)
  }

  // Validation applied well past the filled rows so pasting a block keeps it.
  const VALIDATED_ROWS = 400
  const peopleList = range('A', template.people.length)
  const methodList = range('B', template.methods.length)
  const procedureList = range('C', template.procedures.length)

  for (let r = 2; r <= VALIDATED_ROWS; r += 1) {
    if (peopleList) {
      procedures.getCell(`A${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: peopleList }
      methods.getCell(`A${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: peopleList }
    }
    if (procedureList) {
      procedures.getCell(`B${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: procedureList }
    }
    if (methodList) {
      methods.getCell(`B${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: methodList }
    }
    methods.getCell(`C${r}`).dataValidation = {
      type: 'whole',
      operator: 'greaterThan',
      allowBlank: true,
      formulae: [0],
      showErrorMessage: true,
      error: 'Preference is a whole number of 1 or more. 1 is the first choice.',
    }
    methods.getCell(`D${r}`).dataValidation = {
      type: 'decimal',
      operator: 'between',
      allowBlank: true,
      formulae: [0, 100],
      showErrorMessage: true,
      error: 'Proficiency is a percentage between 0 and 100.',
    }
  }

  for (const ws of [procedures, methods]) {
    ws.getRow(1).font = { bold: true }
    ws.views = [{ state: 'frozen', ySplit: 1 }]
  }
  // Greyed, not hidden: it governs which record a row writes to, and data that
  // decides behaviour while being invisible is worse than a column nobody
  // needs to read.
  procedures.getColumn('procedureId').font = { color: { argb: 'FF9CA3AF' } }
  methods.getColumn('methodId').font = { color: { argb: 'FF9CA3AF' } }

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>
}
